/**
 * Persistent lock-on (ล็อกเป้า, round 4): lock a foe with Tab or a click and it stays until it is let go. The per-move hold
 * lock of systems/lockOn.ts is unchanged; this one feeds its `lockedId()` so a volley goes to the locked foe too.
 *
 * - Commands (`lockOn`): `set` a target (a hostile, or the other fighter of a duel), `next` (Tab: the next hostile in reach,
 *   nearest first, round again; none: a notice), `off`.
 * - While locked the character always faces the target and the target is `targetId` (every move and the basic cut aim at it).
 *   The client turns A/D into a circle round the target and W/S into closing in and backing off (ui/lockOn.ts).
 * - It lets go when the target is dead or gone, or farther than `DUEL_LOCK.drop`, or the player dies.
 * - Move-to-cast: a move pressed while a hostile is locked (or targeted) and out of that move's reach is queued; the character
 *   walks straight at the target (normal walking, walls and all), recomputing as it moves, and casts the moment the move is in
 *   reach (melee moves close to their reach, ranged ones only to their range). A movement key, another move, a guard, a
 *   dodge or a jump cancels it, as does the target going, a stagger, or `DUEL_LOCK.queueSeconds`. (The basic cut already
 *   closes in: that is `engage`.)
 * - Locked form (ท่าล็อกเป้า, paired animation): a lock move (the data's `lockForm`, data/lockForms.ts)
 *   that takes effect on its target puts BOTH in a form for `DUEL_LOCK.formMin` or the move's strikes and tail, whichever is longer: the caster performs it (with a short lunge), the target
 *   plays its receive beats (hit, spin, knock-back a real displacement, kneel or fall, recover), and neither can move or act (a command is dropped, a guard is not raised, a dodge
 *   is not made) until it ends; then both have control again. The blow's own strikes are the form's hit frames. A guard does
 *   not stop a form: a guarding target is held in it still guarding (blows are cut as usual, no guard points drain, each guarded
 *   blow adds ความโกรธ); a counter stance open when it would connect answers the blow instead (outcome `countered`, no form); the caster
 *   staggered breaks it. The bout clock keeps running.
 * - Duel v3 P3 (systems/duelLockOpen.ts, through `setFormGate`): in a bout's fight a form starts only on an OPENED foe
 *   (a guarding or neutral one is not held); a move with a routine (data/lockRoutines.ts) holds for the routine's length, its
 *   lunge over the catch, and cannot be broken; `cut` lets the held foe out (ดิ้นหลุด).
 * Opt-in with `setDuelV2` (the duel clone); the main game never locks.
 */
import { DUEL_FLAGS, DUEL_LOCK, DUEL_V2 } from '../data/duelMatch';
import type { FormBeat, FormBeatKind, LockFormDef } from '../data/lockForms';
import { lockRoutineOf } from '../data/lockRoutines';
import { SKILLS, type SkillDef } from '../data/skills';
import { dist, type Entity, type PlayerCommand, type WorldEvent } from '../world';
import { skillBarOf } from './skillBar';
import type { SystemHost, WorldSystem } from './types';

/** Commands (part of `PlayerCommand`). */
export type LockOnCommand =
  | { t: 'lockOn'; op: 'set' | 'next' | 'off'; id?: number }
  /** Locked with the right button held: face the camera's way (dx, dy) instead of the target; 0, 0 lets go (systems/lockLook.ts). */
  | { t: 'lockOn'; op: 'look'; dx: number; dy: number };

/** What a player cannot do held in a locked form. */
const FROZEN = new Set<PlayerCommand['t']>([
  'moveDir',
  'moveTo',
  'skill',
  'block',
  'dash',
  'jump',
  'attack',
  'engage',
  'sprint',
  'mount',
  'meditate',
  'sheathe',
  'interact',
  'fly',
]);

const SYSTEMS = new WeakMap<object, DuelLockSystem>();
/** Says whether a lock move's blow may start its form (duel v3 P3: only on an opened foe, systems/duelLockOpen.ts). */
type FormGate = (caster: Entity, target: Entity, s: SkillDef) => boolean;
const GATES = new WeakMap<object, FormGate>();
export const setFormGate = (w: object, g: FormGate | null): void => void (g ? GATES.set(w, g) : GATES.delete(w));
export const duelLockOf = (w: object): DuelLockSystem | null => SYSTEMS.get(w) ?? null;

export class DuelLockSystem implements WorldSystem {
  readonly name = 'duelLock';
  /** Player entity id -> the id locked. */
  private readonly locks = new Map<number, number>();
  /** Caster id -> the form under way: its target, seconds run, its length. */
  private readonly forms = new Map<
    number,
    { tgt: number; t: number; len: number; guard: boolean; def: LockFormDef; ax: number; ay: number; cx: number; cy: number; sid: string; hard: boolean }
  >();
  /** Player entity id -> the move waiting to come into reach: its bar slot, the target, seconds waited. */
  private readonly queue = new Map<number, { slot: number; tgt: number; t: number }>();

  constructor(private readonly h: SystemHost) {}

  readonly commands = {
    lockOn: (c: LockOnCommand) => c.op !== 'look' && this.command(c),
  };

  setup(): void {
    SYSTEMS.set(this.h.world, this);
  }

  /** Whom a player has locked, or null. */
  lockedOf(playerId: number): number | null {
    return this.locks.get(playerId) ?? null;
  }

  /** The current player's lock (what `LockOnSystem.lockedId()` falls back on). */
  lockedId(): number | null {
    return this.lockedOf(this.h.world.player.id);
  }

  /** Whether an entity is held in a locked form (as the caster or the target). */
  inForm(id: number): boolean {
    for (const [c, f] of this.forms) if (c === id || f.tgt === id) return true;
    return false;
  }

  /** The form an entity is held in: [caster, target, share run (0-1)], or null. */
  formOf(id: number): [number, number, number] | null {
    for (const [c, f] of this.forms) if (c === id || f.tgt === id) return [c, f.tgt, Math.min(1, f.t / f.len)];
    return null;
  }

  /** The form a caster runs: its target, the move, seconds run and its length (null: none). */
  formInfo(casterId: number): { tgt: number; skillId: string; t: number; len: number } | null {
    const f = this.forms.get(casterId);
    return f ? { tgt: f.tgt, skillId: f.sid, t: f.t, len: f.len } : null;
  }

  /**
   * Re-aims a caster's form along caster → target as they stand now (a routine's `pass` beat carried the caster to the far
   * side: the knock-back beats after it throw the foe away from the caster, not into it).
   */
  turnAxis(casterId: number): void {
    const f = this.forms.get(casterId);
    const c = this.h.world.get(casterId);
    const t = f ? this.h.world.get(f.tgt) : undefined;
    if (!f || !c || !t) return;
    const l = dist(c, t);
    if (l < 1e-6) return;
    f.ax = (t.x - c.x) / l;
    f.ay = (t.y - c.y) / l;
  }

  /** Cuts a caster's form short (the target broke out): both are let go at once. */
  cut(casterId: number): void {
    const f = this.forms.get(casterId);
    if (!f) return;
    this.forms.delete(casterId);
    const t = this.h.world.get(f.tgt);
    if (t) {
      t.stunT = 0;
      t.hitT = 0;
    }
  }

  /** Whether a move is a lock move (ท่าล็อกเป้า): it has a form in data/lockForms.ts. */
  static isForm(s: SkillDef): boolean {
    return !!s.lockForm;
  }

  /**
   * What an entity in a form is doing: its role, the beat it is in (and how far through it, 0-1), how far through the whole
   * form, and the direction away from the caster. The client draws the beat; null when not in a form.
   */
  poseOf(id: number): { role: 'caster' | 'target'; kind: FormBeatKind | 'perform'; k: number; u: number; ax: number; ay: number } | null {
    for (const [c, f] of this.forms) {
      if (c !== id && f.tgt !== id) continue;
      const u = Math.min(1, f.t / f.len);
      if (c === id) return { role: 'caster', kind: 'perform', k: u, u, ax: f.ax, ay: f.ay };
      const b = f.def.receive.find((x: FormBeat) => u >= x.from && u < x.to) ?? f.def.receive[f.def.receive.length - 1]!;
      return { role: 'target', kind: b.kind, k: Math.max(0, Math.min(1, (u - b.from) / Math.max(1e-6, b.to - b.from))), u, ax: f.ax, ay: f.ay };
    }
    return null;
  }

  /** The bar slot of the move a player has queued (walking to the target), or null. */
  queuedOf(playerId: number): number | null {
    return this.queue.get(playerId)?.slot ?? null;
  }

  /** Locks `tgtId` for `playerId` without asking (the duel locks the other fighter at the countdown). */
  set(playerId: number, tgtId: number | null): void {
    if (tgtId === null) this.locks.delete(playerId);
    else this.locks.set(playerId, tgtId);
  }

  /** A target the current player may lock: alive, not themselves, a hostile (or the other fighter of a duel), in range. */
  private lockable(p: Entity, e: Entity | undefined, reach: number): e is Entity {
    if (!e || e.dead || e === p || e.kind === 'pet' || e.kind === 'ambient') return false;
    const w = this.h.world;
    const duel = w.pvpBout && (w.pvpBout.a === p.id || w.pvpBout.b === p.id) && (w.pvpBout.a === e.id || w.pvpBout.b === e.id);
    return (w.isHostile(p, e) || !!duel) && dist(p, e) <= reach;
  }

  /** A move pressed out of reach is queued instead; a movement key (and the like) cancels the queue. */
  refuse(cmd: PlayerCommand): boolean {
    if (!DUEL_FLAGS.v2) return false;
    const w = this.h.world;
    const p = w.player;
    // Held in a form: every command of the player's is dropped (and a queued move with them).
    if (this.inForm(p.id) && FROZEN.has(cmd.t)) {
      this.queue.delete(p.id);
      return true;
    }
    const q = this.queue.get(p.id);
    if (cmd.t === 'skill') {
      // Any new press ends the old queue; this one may start its own.
      this.queue.delete(p.id);
      const id = skillBarOf(w.progress)[cmd.slot];
      const s = id ? SKILLS[id] : undefined;
      if (!s || !id || p.dead || cmd.at || (p.cooldowns[id] ?? 0) > 0 || p.stunT > 0) return false;
      // A move pressed is one blow: the basic cut is held back from this very tick (no skill-and-cut on one press).
      p.attackT = Math.max(p.attackT, DUEL_V2.skillGap);
      // Only the lock moves (ท่าล็อกเป้า, data/lockForms.ts) walk to the target.
      if (!s.lockForm) return false;
      const reach = s.range || s.aoe || 0;
      if (reach <= 0) return false;
      const lockedId = this.locks.get(p.id);
      const t = w.get(lockedId ?? p.targetId);
      if (!this.lockable(p, t, 40) || dist(p, t) <= reach * 0.95) return false;
      this.queue.set(p.id, { slot: cmd.slot, tgt: t.id, t: 0 });
      return true;
    }
    if (!q) return false;
    const stop =
      (cmd.t === 'moveDir' && (cmd.dx !== 0 || cmd.dy !== 0)) ||
      cmd.t === 'moveTo' ||
      (cmd.t === 'block' && cmd.on) ||
      cmd.t === 'dash' ||
      cmd.t === 'jump' ||
      cmd.t === 'untarget' ||
      (cmd.t === 'lockOn' && cmd.op !== 'next');
    if (stop) this.queue.delete(p.id);
    return false;
  }

  /** Walks each queued move's caster at its target, and casts once it is in reach. */
  private approach(dt: number): void {
    const w = this.h.world;
    for (const [pid, q] of [...this.queue]) {
      const p = w.get(pid);
      const t = w.get(q.tgt);
      const inp = w.players.get(pid)?.input;
      const id = skillBarOf(w.players.get(pid)?.progress ?? w.progress)[q.slot];
      const s = id ? SKILLS[id] : undefined;
      q.t += dt;
      if (!p || p.dead || p.stunT > 0 || !t || t.dead || !inp || !s || q.t > DUEL_LOCK.queueSeconds || (p.cooldowns[id!] ?? 0) > 0) {
        this.queue.delete(pid);
        if (inp) Object.assign(inp, { dx: 0, dy: 0 });
        continue;
      }
      const reach = s.range || s.aoe || 0;
      const d = dist(p, t);
      if (d <= reach * 0.9) {
        this.queue.delete(pid);
        Object.assign(inp, { dx: 0, dy: 0 });
        w.apply({ t: 'skill', slot: q.slot }, pid);
        continue;
      }
      Object.assign(inp, { dx: (t.x - p.x) / d, dy: (t.y - p.y) / d, engage: false });
      p.path = null;
    }
  }

  private command(c: { op: 'set' | 'next' | 'off'; id?: number }): void {
    if (!DUEL_FLAGS.v2) return;
    const w = this.h.world;
    const p = w.player;
    if (p.dead) return;
    if (c.op === 'off') {
      this.locks.delete(p.id);
      return;
    }
    if (c.op === 'set') {
      const t = w.get(c.id ?? -1);
      if (this.lockable(p, t, DUEL_LOCK.reach)) {
        this.locks.set(p.id, t.id);
        p.targetId = t.id;
      }
      return;
    }
    // next: nearest first (ties by id), the one after the current, round again
    const list = [...w.entities.values()]
      .filter((e) => this.lockable(p, e, DUEL_LOCK.reach))
      .sort((a, b) => dist(p, a) - dist(p, b) || a.id - b.id);
    if (!list.length) {
      this.h.notice('ไม่มีเป้าหมายในระยะ');
      return;
    }
    const cur = list.findIndex((e) => e.id === this.locks.get(p.id));
    const pick = list[(cur + 1) % list.length]!;
    this.locks.set(p.id, pick.id);
    p.targetId = pick.id;
  }

  /**
   * A lock move's FIRST blow that lands (a `hit` event of outcome `hit` or `blocked`: a guard does not stop a form, per the user's
   * rule) puts both in a form. A dodge, a counter or a guard shatter starts none: a dodge escapes it, and the caster plays the whiff.
   */
  observe(e: WorldEvent): void {
    if (!DUEL_FLAGS.v2 || e.type !== 'hit' || (e.outcome !== 'hit' && e.outcome !== 'blocked')) return;
    const s = SKILLS[e.skillId];
    if (!s || !DuelLockSystem.isForm(s)) return;
    const w = this.h.world;
    const c = w.get(e.srcId);
    const t = w.get(e.tgtId);
    if (!c || !t || c.dead || t.dead || c === t || this.inForm(c.id) || this.inForm(t.id)) return;
    if (dist(c, t) > s.range + 1 || !w.isHostile(c, t)) return;
    if (GATES.get(w)?.(c, t, s) === false) return;
    // A long routine (data/lockRoutines.ts) holds for its own length and cannot be broken once it runs.
    const rt = lockRoutineOf(s.id);
    const len = rt ? rt.r.len : Math.max(DUEL_LOCK.formMin, DUEL_LOCK.formStrike + Math.max(0, (s.hits ?? 1) - 1) * 0.15 + DUEL_LOCK.formTail);
    const guard = t.blocking;
    const l = dist(c, t) || 1;
    this.forms.set(c.id, { tgt: t.id, t: 0, len, guard, def: rt?.form ?? s.lockForm!, ax: (t.x - c.x) / l, ay: (t.y - c.y) / l, cx: c.x, cy: c.y, sid: s.id, hard: !!rt });
    // The target takes the receive sequence (a stagger; a guarding target keeps its guard and flinches), what it was
    // winding up broken off.
    if (guard) t.hitT = Math.max(t.hitT, len);
    else t.stunT = Math.max(t.stunT, len);
    t.cast = null;
    this.hold(c.id);
    this.hold(t.id, guard);
  }

  /** Stills a player's controls for the form (the commands are dropped in `refuse`). */
  private hold(id: number, guard = false): void {
    const w = this.h.world;
    const inp = w.players.get(id)?.input;
    if (inp) Object.assign(inp, { dx: 0, dy: 0, block: guard, engage: false, sprint: false });
    this.queue.delete(id);
    const e = w.get(id);
    if (e) Object.assign(e, { path: null, dashT: 0, ...(guard ? {} : { blocking: false }) });
  }

  private runForms(dt: number): void {
    const w = this.h.world;
    for (const [cid, f] of [...this.forms]) {
      const c = w.get(cid);
      const t = w.get(f.tgt);
      f.t += dt;
      // Broken out of only if the caster is staggered (or gone); otherwise it runs its length.
      const broken = !c || !t || c.dead || t.dead || (c.stunT > 0 && f.t > 0.05 && !f.hard);
      if (broken || f.t >= f.len) {
        this.forms.delete(cid);
        if (t && !broken) {
          t.stunT = Math.min(t.stunT, 0.05);
          t.hitT = 0;
          // (a guard held through the form is let go of: the key was dropped, the player raises it again)
          const inp = w.players.get(f.tgt)?.input;
          if (inp && f.guard) inp.block = false;
        }
        continue;
      }
      this.hold(cid);
      this.hold(f.tgt, f.guard);
      // The caster's lunge (the first fifth), the target's knock-back beats: real displacements, walls and all.
      const lw = f.hard ? Math.min(f.len * 0.2, 0.4) : f.len * 0.2;
      if (f.t < lw && f.def.lunge > 0 && dist(c, t) > 1) {
        const step = Math.min((f.def.lunge / lw) * dt, dist(c, t) - 1);
        this.h.move(c, f.ax * step, f.ay * step);
      }
      const u = f.t / f.len;
      for (const b of f.def.receive)
        if (b.kind === 'knock' && b.dist && u >= b.from && u < b.to)
          this.h.move(t, f.ax * (b.dist / ((b.to - b.from) * f.len)) * dt, f.ay * (b.dist / ((b.to - b.from) * f.len)) * dt);
      this.h.face(c, t);
      this.h.face(t, c);
    }
  }

  step(dt = 1 / 60): void {
    if (!DUEL_FLAGS.v2) return;
    this.runForms(dt);
    this.approach(dt);
    if (!this.locks.size) return;
    const w = this.h.world;
    for (const [pid, tid] of [...this.locks]) {
      const p = w.get(pid);
      const t = w.get(tid);
      if (!p || p.dead || !t || t.dead || dist(p, t) > DUEL_LOCK.drop) {
        this.locks.delete(pid);
        continue;
      }
      p.targetId = tid;
      this.h.face(p, t);
    }
  }
}
